import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { AuditHistory } from "@/components/audit/AuditHistory";
import {
  VehicleFieldModesEditor,
  resolveVehicleFieldEditorState,
  type VehicleFieldEditorState,
} from "@/components/arrival-types/VehicleFieldModesEditor";
import { api } from "@/lib/apiClient";
import type { ArrivalTypeRecord } from "@/types/camping";

interface FormState {
  name: string;
  name_en: string;
  tourist_abbreviation: string;
  sort_order: string;
  requires_vehicle_length: boolean;
  vehicle_fields: VehicleFieldEditorState;
  online_booking_enabled: boolean;
  is_active: boolean;
}

function toForm(record: ArrivalTypeRecord): FormState {
  return {
    name: record.name,
    name_en: record.name_en ?? "",
    tourist_abbreviation: record.tourist_abbreviation ?? "",
    sort_order: String(record.sort_order),
    requires_vehicle_length: record.requires_vehicle_length,
    vehicle_fields: resolveVehicleFieldEditorState(record.code, record),
    online_booking_enabled: record.online_booking_enabled,
    is_active: record.is_active,
  };
}

function serializeTextConfig(
  config: Partial<Record<string, string>>,
): Record<string, string> | null {
  const entries = Object.entries(config).filter(
    (entry): entry is [string, string] => Boolean(entry[1]?.trim()),
  );
  if (entries.length === 0) return null;
  return Object.fromEntries(entries.map(([key, value]) => [key, value.trim()]));
}

export interface EditArrivalTypeModalProps {
  open: boolean;
  arrivalType: ArrivalTypeRecord | null;
  onClose: () => void;
  onSaved: () => void;
  canEdit: boolean;
  touristIntegrationEnabled?: boolean;
}

export function EditArrivalTypeModal({
  open,
  arrivalType,
  onClose,
  onSaved,
  canEdit,
  touristIntegrationEnabled = false,
}: EditArrivalTypeModalProps) {
  const [form, setForm] = useState<FormState | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !arrivalType) return;
    setForm(toForm(arrivalType));
    setError(null);
    setSubmitting(false);
  }, [open, arrivalType?.id]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!arrivalType || !form || !canEdit) return;
    setError(null);
    setSubmitting(true);
    try {
      await api.patch(`/api/v1/arrival-types/${arrivalType.id}`, {
        name: form.name.trim(),
        name_en: form.name_en.trim() || null,
        tourist_abbreviation: touristIntegrationEnabled
          ? form.tourist_abbreviation.trim() || null
          : undefined,
        sort_order: Number(form.sort_order) || 0,
        requires_vehicle_length: form.requires_vehicle_length,
        vehicle_field_modes: form.vehicle_fields.modes,
        vehicle_field_tooltips: serializeTextConfig(form.vehicle_fields.tooltips),
        vehicle_field_max_values: serializeTextConfig(form.vehicle_fields.max_values),
        vehicle_field_max_messages: serializeTextConfig(form.vehicle_fields.max_messages),
        online_booking_enabled: form.online_booking_enabled,
        is_active: form.is_active,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Speichern");
    } finally {
      setSubmitting(false);
    }
  }

  if (!arrivalType || !form) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={arrivalType.name}
      description={canEdit ? "Anreiseart bearbeiten" : "Details (nur Lesen)"}
    >
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
        <p className="text-sm text-label-secondary">
          Code: <span className="font-mono text-label">{arrivalType.code}</span>
        </p>
        <label className="flex flex-col gap-1 text-sm">
          Bezeichnung (Deutsch)
          <input
            required
            disabled={!canEdit}
            className="eh-input"
            value={form.name}
            onChange={(e) => setForm((f) => (f ? { ...f, name: e.target.value } : f))}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Bezeichnung (Englisch)
          <input
            disabled={!canEdit}
            className="eh-input"
            value={form.name_en}
            onChange={(e) => setForm((f) => (f ? { ...f, name_en: e.target.value } : f))}
            placeholder="Optional für englische Buchungsstrecke"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Sortierung
          <input
            type="number"
            disabled={!canEdit}
            className="eh-input w-32"
            value={form.sort_order}
            onChange={(e) => setForm((f) => (f ? { ...f, sort_order: e.target.value } : f))}
          />
        </label>
        {touristIntegrationEnabled ? (
          <label className="flex flex-col gap-1 text-sm">
            Tourist-Kürzel (PMS carnumber)
            <input
              disabled={!canEdit}
              className="eh-input font-mono uppercase"
              value={form.tourist_abbreviation}
              onChange={(e) =>
                setForm((f) => (f ? { ...f, tourist_abbreviation: e.target.value } : f))
              }
              placeholder="z. B. WW, MC, ZELT"
              maxLength={32}
            />
            <span className="text-xs text-label-secondary">
              Wird im PMS-Import als carnumber übergeben (Anreiseart der Buchung).
            </span>
          </label>
        ) : null}
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            disabled={!canEdit}
            checked={form.requires_vehicle_length}
            onChange={(e) =>
              setForm((f) => (f ? { ...f, requires_vehicle_length: e.target.checked } : f))
            }
          />
          Fahrzeuglänge erforderlich (Legacy-Fallback)
        </label>
        <VehicleFieldModesEditor
          code={arrivalType.code}
          value={form.vehicle_fields}
          touristIntegrationEnabled={touristIntegrationEnabled}
          onChange={(vehicle_fields) => setForm((f) => (f ? { ...f, vehicle_fields } : f))}
          disabled={!canEdit}
        />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            disabled={!canEdit}
            checked={form.online_booking_enabled}
            onChange={(e) =>
              setForm((f) => (f ? { ...f, online_booking_enabled: e.target.checked } : f))
            }
          />
          In Online-Buchung wählbar
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            disabled={!canEdit}
            checked={form.is_active}
            onChange={(e) => setForm((f) => (f ? { ...f, is_active: e.target.checked } : f))}
          />
          Aktiv
        </label>
        <AuditHistory entityType="arrival_type" entityId={arrivalType.id} active={open} />
        {error ? <p className="text-sm text-status-red">{error}</p> : null}
        <div className="eh-modal-actions flex flex-wrap justify-end gap-2 border-t border-separator pt-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            {canEdit ? "Abbrechen" : "Schließen"}
          </Button>
          {canEdit ? (
            <Button type="submit" loading={submitting}>
              Speichern
            </Button>
          ) : null}
        </div>
      </form>
    </Modal>
  );
}
